/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Button, useNotification, type ButtonProps } from '@az/starc-ui';
import { useReviewFormData } from '../../api/getReviewFormData';
import { useImperativeHandle, useRef } from 'react';
import { useLabels } from '@/hooks/useLabels';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { routePaths } from '@/constants/routePaths';
import logger from '@/utils/logger';
import type { SubmitReviewDefaultValues, SubmitReviewPageType } from '../../interface';
import { CUSTOMER_REVIEWS_HASH } from '@/features/customReviews/constants/sectionHash';
import { useInitiateSubmitMutation } from '@/features/customReviews/api/postInitiateSubmit';
import { useLocale } from '@/hooks/useLocale';
import { localStorage } from '@/utils/localStorage';
import { normalizeQueryParam } from '@/features/customReviews/utils/normalizeQueryParams';
import { normalizeErrorForLogging } from '@/utils/errorsHandling/normalizeErrorForLogging';
import { useAppData } from '@/hooks/useAppData';

export type SubmitReviewButtonProps = {
  productId?: string;
  productName?: string;
  productImageUrl?: string;
  label: React.ReactElement | string | boolean | null | undefined;
  defaultValues?: SubmitReviewDefaultValues;
  startIcon?: ButtonProps['startIcon'];
  variant?: 'primary' | 'secondary' | 'pill-secondary';
  className?: string;
  pageType?: SubmitReviewPageType;
  onClose?: VoidFunction;
  onSuccess?: VoidFunction;
  onDuplicated?: VoidFunction;
  ref?: React.RefObject<unknown>;
};

const labelMap = {
  errorNotificationTitle: 'label_reviews_submit_reviewErrorNotificationTitle',
  errorNotificationText: 'label_reviews_submit_errorNotificationText',
};

export const SubmitReviewButton = function SubmitReviewButton({
  ref,
  productId,
  productName,
  productImageUrl,
  defaultValues,
  label,
  startIcon,
  variant,
  className,
  pageType,
  onDuplicated,
}: SubmitReviewButtonProps) {
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const locale = useLocale();
  const labels = useLabels(labelMap);

  const writeReviewButtonRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => writeReviewButtonRef.current);

  const notification = useNotification();

  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const {
    mutateAsync: getReviewFormData,
    hasSubmitted,
    isPending,
  } = useReviewFormData({
    productId,
  });

  const { mutateAsync: getProgressiveFormData, isPending: isInitiating } =
    useInitiateSubmitMutation();

  const openSubmitReview = async () => {
    try {
      if (!productId || !pageType) {
        return;
      }

      const flow =
        typeof legacyRouter.query.flow === 'string' ? legacyRouter.query.flow : undefined;
      const useProgressiveSubmission = userAuthenticated || flow === 'email';

      const displaycode = normalizeQueryParam(legacyRouter.query.displaycode) || undefined;

      const progressiveForm = useProgressiveSubmission
        ? await getProgressiveFormData({
            productIds: [productId],
            locale,
            pageType,
            userToken:
              defaultValues?.uas ?? localStorage.getItem('reviewSubmissionId') ?? undefined,
            ...(displaycode ? { displaycode } : {}),
          })
        : null;

      const formData = useProgressiveSubmission ? null : await getReviewFormData();

      const hasSubmitted = progressiveForm ? progressiveForm.hasSubmitted : formData?.hasSubmitted;

      const progressiveReview =
        progressiveForm?.data.response?.productFormData?.[productId]?.review;

      const hasSubmittedContent = Boolean(
        progressiveReview?.reviewText || progressiveReview?.duplicateReview,
      );

      if (hasSubmitted || hasSubmittedContent) {
        onDuplicated?.();

        const id = notification.show({
          type: 'error',
          title: labels.errorNotificationTitle,
          text: labels.errorNotificationText,
          position: 'top-end',
          hasCloseButton: true,
          onClose: () => notification.hide(id),
        });
        return;
      }
      const isPDP = legacyRouter.pathname === '/products/[...slug]';

      const prevUrl =
        isPDP && !legacyRouter.asPath.endsWith(CUSTOMER_REVIEWS_HASH)
          ? `${legacyRouter.asPath}${CUSTOMER_REVIEWS_HASH}`
          : undefined;

      const pathname = `${routePaths.writeReviewPath}/${productId}`;
      const queryObj = Object.fromEntries(
        Object.entries({
          prevUrl,
          pageType,
          flow: legacyRouter.query.flow,
          displaycode: legacyRouter.query.displaycode,
          productIds: legacyRouter.query.productIds,
          usernickname: legacyRouter.query.usernickname,
          productName,
          productImageUrl,
          ...(defaultValues ?? {}),
        }).filter(([, v]) => v !== undefined && v !== null && v !== ''),
      );
      const params = new URLSearchParams(
        Object.entries(queryObj).map(([key, value]) => [key, String(value)]),
      ).toString();
      void router.push(`${pathname}?${params}`);
    } catch (error) {
      logger.error({
        message: 'Failed to get review form data',
        meta: {
          source: 'SubmitReviewButton',
          error: normalizeErrorForLogging(error),
        },
      });
    }
  };

  return (
    <>
      <Button
        className={className}
        variant={variant ?? 'secondary'}
        onClick={openSubmitReview}
        ref={writeReviewButtonRef}
        loading={isPending || isInitiating}
        startIcon={startIcon}
        disabled={hasSubmitted || !productId}
      >
        {label}
      </Button>
    </>
  );
};
